import './index.css';

import { ReactSession } from 'react-client-session';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingIcons from 'react-loading-icons';

import Header from '../Header';
import Charts, { PriceChartApiResponse } from '../Charts';
import Info from '../Info';
import Control from '../Control';
import Transactions from '../Transactions';
import {
  Bot,
  Subscription,
  SubscriptionBalance,
  Tabs,
  TimeFrame,
  TransactionDetailsApiResponse,
} from '../types';
import axios, { Method } from 'axios';
import { ProfitChartApiResponse } from '../Charts/types';
import { Transaction } from '../../Overview/types';
import ServicePaths from '../../../bridge/utils/ServicePaths';
import { getSelectedTimeFrame } from '../Charts/PriceChart/helpers';
import useRequest from '../../../hooks/useRequest';

interface Props {
  isLoading: boolean;
  bot?: Bot;
  activeTab: Tabs;
  subscription?: Subscription;
  subscriptionBalance?: SubscriptionBalance;
}

const Content = (props: Props) => {
  const { isLoading, bot, subscription, subscriptionBalance, activeTab } =
    props;

  const [candles, setCandles] = useState<PriceChartApiResponse | undefined>(
    undefined,
  );
  const [isCandlesLoading, setIsCandlesLoading] = useState(true);
  const [selectedTimeFrameIndex, setSelectedTimeFrameIndex] = useState(2);

  const [profitChartData, setProfitChartData] = useState<
    ProfitChartApiResponse | undefined
  >(undefined);
  const [isProfitChartLoading, setIsProfitChartLoading] = useState(true);
  const [showCurrentOrder, setShowCurrentOrder] = useState(false);

  const [latestTransaction, setLatestTransaction] =
    useState<Transaction | null>(null);

  const params = useMemo(
    () => ({ subscriptionId: subscription?.id }),
    [subscription?.id],
  );
  const options = useMemo(
    () => ({
      method: 'POST' as Method,
      shouldFetch: !!subscription?.id,
    }),
    [subscription?.id],
  );

  const {
    data: transactions,
    isValidating,
  }: {
    data: TransactionDetailsApiResponse | undefined;
    isValidating: boolean;
  } = useRequest(ServicePaths.DATA.GetClientBotTransactions, params, options);

  const timeFrame = useMemo(() => {
    if (!bot) {
      return TimeFrame.OneWeek;
    }

    return getSelectedTimeFrame(bot, selectedTimeFrameIndex);
  }, [bot, selectedTimeFrameIndex]);

  const candleSticksLatestOrder = useMemo(() => {
    if (!latestTransaction) {
      return undefined;
    }

    return {
      orderType: latestTransaction.orderType,
      timestamp: latestTransaction.transactionTimestamp,
    };
  }, [latestTransaction]);

  const earliestTimestamp = useMemo(
    () =>
      transactions?.allTransactions.length
        ? transactions.allTransactions[transactions.allTransactions.length - 1]
            .transactionTimestamp
        : bot?.dateCreated
        ? new Date(bot.dateCreated).valueOf()
        : undefined,
    [bot?.dateCreated, transactions?.allTransactions],
  );

  /** Fetch candles */
  useEffect(() => {
    const fetch = async () => {
      if (!bot || !transactions?.compactTransactions?.length) {
        return;
      }

      setIsCandlesLoading(true);

      const { baseAsset, quoteAsset, defaultExchange } = bot;
      const timeFrame = getSelectedTimeFrame(bot, selectedTimeFrameIndex);

      const startTimestamp =
        timeFrame === bot.timeFrame.multiOrder
          ? transactions.allTransactions[0].transactionTimestamp
          : transactions.compactTransactions[
              transactions.compactTransactions.length - 1
            ].transactionTimestamp;
      const endTimestamp = new Date().valueOf();

      const { data } = await axios({
        url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
        params: {
          baseAsset,
          quoteAsset,
          timeFrame,
          startTimestamp,
          endTimestamp,
          includeLatestCandle: true,
          exchange: defaultExchange,
        },
      });

      setCandles(data);
      setIsCandlesLoading(false);
    };

    if (!bot) {
      return;
    }

    fetch();
  }, [
    bot,
    selectedTimeFrameIndex,
    transactions?.allTransactions,
    transactions?.compactTransactions,
  ]);

  useEffect(() => {
    const fetch = async () => {
      if (!subscription) {
        return;
      }

      const { id } = subscription;

      const { data } = await axios({
        url: ServicePaths.DATA.GetClientBotProfitCharts,
        method: 'POST',
        data: {
          subscriptionId: id,
          startTimestamp: undefined,
          showCurrentOrder: showCurrentOrder || undefined,
        },
      });

      setProfitChartData(
        data && Object.keys(data).length !== 0 ? data : undefined,
      );
      setIsProfitChartLoading(false);
    };

    if (!subscription) {
      return;
    }

    fetch();
  }, [subscription, showCurrentOrder]);

  const fetchTransactions = useCallback(async () => {
    if (!subscription) {
      return;
    }

    const { id } = subscription;

    const { data } = await axios({
      url: ServicePaths.DATA.GetClientLatestTransaction,
      method: 'POST',
      data: {
        subscriptionId: id,
      },
    });

    setLatestTransaction(data);
  }, [subscription]);

  useEffect(() => {
    if (!!subscription) {
      fetchTransactions();
    }
  }, [fetchTransactions, subscription]);

  const latestPrice = useMemo(
    () => (candles ? candles.normal[candles.normal.length - 1] : undefined),
    [candles],
  );

  // useEffect(() => {
  //   if (!profitChartData) {
  //     return;
  //   }

  //   setTotalValueData(profitChartData.dataPoints.total.stable);
  // }, [profitChartData, setTotalValueData]);

  if (isLoading) {
    return (
      <div className="d-loading-container">
        <LoadingIcons.Grid width={100} />
      </div>
    );
  }

  if (!bot || !subscription || !subscriptionBalance) {
    return (
      <div className="d-loading-container">
        <h1>Bot not found</h1>
      </div>
    );
  }

  return (
    <div className="d-container">
      <Header
        bot={bot}
        subscription={subscription}
        subscriptionBalance={subscriptionBalance}
        latestPrice={latestPrice}
        earliestTimestamp={earliestTimestamp}
      />
      {activeTab === Tabs.Charts && (
        <Charts
          bot={bot}
          candleData={candles}
          profitData={profitChartData}
          selectedTimeFrameIndex={selectedTimeFrameIndex}
          setSelectedTimeFrameIndex={setSelectedTimeFrameIndex}
          isCandlesLoading={isCandlesLoading}
          isProfitChartLoading={isProfitChartLoading}
          showCurrentOrder={showCurrentOrder}
          setShowCurrentOrder={setShowCurrentOrder}
          candleSticksLatestOrder={candleSticksLatestOrder}
          compactTransactions={transactions?.compactTransactions}
        />
      )}
      {activeTab === Tabs.Transactions && (
        <Transactions
          bot={bot}
          timeFrame={timeFrame}
          candleSticksLatestOrder={candleSticksLatestOrder}
          transactions={transactions}
          isValidating={isValidating}
        />
      )}
      {activeTab === Tabs.Control && <Control bot={bot} />}
      {activeTab === Tabs.ExtraInfo && <Info bot={bot} />}
    </div>
  );
};

export default React.memo(Content);
